import gsap from "gsap";

class AuftragsFinderWizard {
  constructor() {
    this.currentStep = 0;
    this.answers = {};
    this.steps = [];
    this.visibleSteps = [];
    this.storageKey = 'auftrags-finder-data';

    this.init();
  }

  init() {
    // Load data from script tag
    const dataElement = document.getElementById("wizard-data");
    if (!dataElement) {
      console.error("Wizard data not found");
      return;
    }

    try {
      this.data = JSON.parse(dataElement.textContent);
      this.steps = this.data.steps;
    } catch (e) {
      console.error("Failed to parse wizard data:", e);
      return;
    }

    // Load saved data from localStorage
    this.loadFromLocalStorage();

    // Get DOM elements
    this.elements = {
      container: document.querySelector("[data-wizard-container]"),
      navigation: document.querySelector(".auftrags-finder__navigation"),
      progress: document.querySelector(".auftrags-finder__progress"),
      progressBar: document.querySelector("[data-wizard-progress]"),
      currentStepText: document.querySelector("[data-wizard-current-step]"),
      totalStepsText: document.querySelector("[data-wizard-total-steps]"),
      backButton: document.querySelector("[data-wizard-back]"),
      nextButton: document.querySelector("[data-wizard-next]"),
      summary: document.querySelector("[data-wizard-summary]"),
      summaryContent: document.querySelector("[data-wizard-summary-content]"),
      success: document.querySelector("[data-wizard-success]"),
      error: document.querySelector("[data-wizard-error]"),
    };

    // Bind event listeners
    this.elements.backButton.addEventListener("click", () => this.goBack());
    this.elements.nextButton.addEventListener("click", () => this.goNext());

    const resetButton = document.querySelector("[data-wizard-reset]");
    const editButton = document.querySelector("[data-wizard-edit]");
    const submitButton = document.querySelector("[data-wizard-submit]");
    const retryButton = document.querySelector("[data-wizard-retry]");

    if (resetButton) resetButton.addEventListener("click", () => this.reset());
    if (editButton) editButton.addEventListener("click", () => this.editAnswers());
    if (submitButton) submitButton.addEventListener("click", () => this.submit());
    if (retryButton) retryButton.addEventListener("click", () => this.retry());

    // Calculate visible steps and render first step
    this.updateVisibleSteps();
    this.renderStep();
    this.updateProgress();
  }

  loadFromLocalStorage() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        this.answers = parsed.answers || {};
        this.currentStep = parsed.currentStep || 0;
      }
    } catch (e) {
      console.error("Failed to load from localStorage:", e);
    }
  }

  saveToLocalStorage() {
    try {
      const dataToSave = {
        answers: this.answers,
        currentStep: this.currentStep,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(dataToSave));
    } catch (e) {
      console.error("Failed to save to localStorage:", e);
    }
  }

  clearLocalStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.error("Failed to clear localStorage:", e);
    }
  }

  updateVisibleSteps() {
    this.visibleSteps = this.steps.filter((step) => {
      if (!step.condition) return true;

      const conditionField = step.condition.field;
      const conditionValue = step.condition.value;

      return this.answers[conditionField] === conditionValue;
    });

    this.elements.totalStepsText.textContent = this.visibleSteps.length;
  }

  renderStep() {
    const step = this.visibleSteps[this.currentStep];
    if (!step) return;

    const stepHtml = this.getStepHtml(step);

    // Animate out old content
    gsap.to(this.elements.container, {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => {
        this.elements.container.innerHTML = stepHtml;
        this.bindStepEvents(step);

        // Update button states after render
        this.updateNavigationButtons();
        this.updateNextButtonState();

        // Animate in new content
        gsap.fromTo(
          this.elements.container,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.3 }
        );
      },
    });
  }

  getStepHtml(step) {
    let html = `<div class="auftrags-finder__step" data-step-id="${step.id}">`;
    html += `<h2 class="auftrags-finder__step-title">${step.title}</h2>`;

    switch (step.type) {
      case "radio":
        html += this.getRadioHtml(step);
        break;
      case "checkbox":
        html += this.getCheckboxHtml(step);
        break;
      case "form":
        html += this.getFormHtml(step);
        break;
    }

    html += `</div>`;
    return html;
  }

  getRadioHtml(step) {
    let html = '<div class="auftrags-finder__options">';

    step.options.forEach((option, index) => {
      const isChecked = this.answers[step.id] === option.value;
      html += `
        <label class="auftrags-finder__option ${isChecked ? 'auftrags-finder__option--selected' : ''}" data-option-index="${index}">
          <input
            type="radio"
            name="${step.id}"
            value="${option.value}"
            ${isChecked ? 'checked' : ''}
            class="auftrags-finder__option-input"
          />
          <div class="auftrags-finder__option-content">
            ${option.icon ? `<span class="auftrags-finder__option-icon">${option.icon}</span>` : ''}
            <div class="auftrags-finder__option-text">
              <div class="auftrags-finder__option-label">${option.label}</div>
              ${option.description ? `<div class="auftrags-finder__option-description">${option.description}</div>` : ''}
            </div>
          </div>
        </label>
      `;
    });

    html += '</div>';
    return html;
  }

  getCheckboxHtml(step) {
    let html = '<div class="auftrags-finder__options auftrags-finder__options--checkbox">';

    const currentAnswers = this.answers[step.id] || [];

    step.options.forEach((option, index) => {
      const isChecked = currentAnswers.includes(option.value);
      html += `
        <label class="auftrags-finder__option ${isChecked ? 'auftrags-finder__option--selected' : ''}" data-option-index="${index}">
          <input
            type="checkbox"
            name="${step.id}"
            value="${option.value}"
            ${isChecked ? 'checked' : ''}
            class="auftrags-finder__option-input"
          />
          <div class="auftrags-finder__option-content">
            <div class="auftrags-finder__option-text">
              <div class="auftrags-finder__option-label">${option.label}</div>
              ${option.description ? `<div class="auftrags-finder__option-description">${option.description}</div>` : ''}
            </div>
            <div class="auftrags-finder__option-checkbox">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </label>
      `;
    });

    html += '</div>';
    return html;
  }

  getFormHtml(step) {
    let html = '<form class="auftrags-finder__form">';

    step.fields.forEach((field) => {
      const value = this.answers[field.id] || '';

      if (field.type === 'checkbox') {
        // Special handling for checkboxes (like privacy consent)
        const isChecked = this.answers[field.id] === true || this.answers[field.id] === 'true';
        html += `<div class="auftrags-finder__form-group auftrags-finder__form-group--checkbox">`;
        html += `
          <label class="auftrags-finder__form-checkbox-label">
            <input
              type="checkbox"
              id="${field.id}"
              name="${field.id}"
              class="auftrags-finder__form-checkbox"
              ${isChecked ? 'checked' : ''}
              ${field.required ? 'required' : ''}
            />
            <span class="auftrags-finder__form-checkbox-text">
              ${field.label}
              ${field.required ? '<span class="auftrags-finder__form-required">*</span>' : ''}
            </span>
          </label>
        `;
        html += `</div>`;
      } else {
        // Regular text inputs and textareas
        html += `<div class="auftrags-finder__form-group">`;
        html += `<label class="auftrags-finder__form-label" for="${field.id}">
          ${field.label}
          ${field.required ? '<span class="auftrags-finder__form-required">*</span>' : ''}
        </label>`;

        if (field.type === 'textarea') {
          html += `<textarea
            id="${field.id}"
            name="${field.id}"
            class="auftrags-finder__form-input auftrags-finder__form-textarea"
            placeholder="${field.placeholder || ''}"
            ${field.required ? 'required' : ''}
            rows="${field.rows || 4}"
          >${value}</textarea>`;
        } else {
          html += `<input
            type="${field.type}"
            id="${field.id}"
            name="${field.id}"
            class="auftrags-finder__form-input"
            placeholder="${field.placeholder || ''}"
            value="${value}"
            ${field.required ? 'required' : ''}
          />`;
        }

        html += `</div>`;
      }
    });

    html += '</form>';
    return html;
  }

  bindStepEvents(step) {
    if (step.type === "radio") {
      const options = this.elements.container.querySelectorAll('input[type="radio"]');
      options.forEach((option) => {
        option.addEventListener("change", (e) => {
          this.answers[step.id] = e.target.value;
          this.updateVisibleSteps();
          this.saveToLocalStorage();
          this.updateNextButtonState();

          // Update visual selection
          const labels = this.elements.container.querySelectorAll('.auftrags-finder__option');
          labels.forEach(label => label.classList.remove('auftrags-finder__option--selected'));
          e.target.closest('.auftrags-finder__option').classList.add('auftrags-finder__option--selected');

          // Automatically go to next step after selection
          setTimeout(() => this.goNext(), 300);
        });
      });
    } else if (step.type === "checkbox") {
      const options = this.elements.container.querySelectorAll('input[type="checkbox"]');
      options.forEach((option) => {
        option.addEventListener("change", (e) => {
          if (!this.answers[step.id]) {
            this.answers[step.id] = [];
          }

          if (e.target.checked) {
            this.answers[step.id].push(e.target.value);
          } else {
            this.answers[step.id] = this.answers[step.id].filter(v => v !== e.target.value);
          }

          this.saveToLocalStorage();
          this.updateNextButtonState();

          // Update visual selection
          if (e.target.checked) {
            e.target.closest('.auftrags-finder__option').classList.add('auftrags-finder__option--selected');
          } else {
            e.target.closest('.auftrags-finder__option').classList.remove('auftrags-finder__option--selected');
          }
        });
      });
    } else if (step.type === "form") {
      const inputs = this.elements.container.querySelectorAll('input, textarea');
      inputs.forEach((input) => {
        // Handle checkboxes differently
        if (input.type === 'checkbox') {
          input.addEventListener("change", (e) => {
            this.answers[e.target.name] = e.target.checked;
            this.saveToLocalStorage();
            this.updateNextButtonState();
          });
        } else {
          input.addEventListener("input", (e) => {
            this.answers[e.target.name] = e.target.value;
            this.saveToLocalStorage();
            this.updateNextButtonState();
          });
        }
      });
    }
  }

  goNext() {
    const currentStepData = this.visibleSteps[this.currentStep];

    // Validate current step
    if (!this.validateStep(currentStepData)) {
      return;
    }

    if (this.currentStep < this.visibleSteps.length - 1) {
      this.currentStep++;
      this.saveToLocalStorage();
      this.renderStep();
      this.updateProgress();
      this.scrollToTop();
    } else {
      // Last step - show summary
      this.showSummary();
    }
  }

  goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.saveToLocalStorage();
      this.renderStep();
      this.updateProgress();
      this.scrollToTop();
    }
  }

  validateStep(step) {
    if (!step.required && step.type !== "form") {
      return true;
    }

    if (step.type === "radio") {
      if (!this.answers[step.id]) {
        this.showError("Bitte wähle eine Option aus.");
        return false;
      }
    } else if (step.type === "form") {
      const requiredFields = step.fields.filter(f => f.required);
      for (let field of requiredFields) {
        const value = this.answers[field.id];

        // Checkbox validation (e.g., privacy consent)
        if (field.type === 'checkbox') {
          if (value !== true) {
            this.showError("Bitte akzeptiere die Datenschutzerklärung.");
            return false;
          }
          continue;
        }

        // Regular field validation
        if (!value || value.trim() === '') {
          this.showError(`Bitte fülle das Feld "${field.label}" aus.`);
          return false;
        }

        // Email validation
        if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            this.showError("Bitte gib eine gültige E-Mail-Adresse ein.");
            return false;
          }
        }
      }
    }

    return true;
  }

  showError(message) {
    // TODO: Implement better error display
    alert(message);
  }

  updateNavigationButtons() {
    this.elements.backButton.disabled = this.currentStep === 0;

    const isLastStep = this.currentStep === this.visibleSteps.length - 1;
    this.elements.nextButton.textContent = isLastStep ? "Weiter zur Zusammenfassung" : "Weiter";
  }

  updateNextButtonState() {
    const currentStepData = this.visibleSteps[this.currentStep];
    if (!currentStepData) return;

    const isValid = this.isStepValid(currentStepData);
    this.elements.nextButton.disabled = !isValid;
  }

  isStepValid(step) {
    // Non-required steps are always valid (except forms)
    if (!step.required && step.type !== "form") {
      return true;
    }

    // Radio buttons
    if (step.type === "radio") {
      return !!this.answers[step.id];
    }

    // Checkboxes (non-required are always valid)
    if (step.type === "checkbox") {
      return true;
    }

    // Form fields
    if (step.type === "form") {
      const requiredFields = step.fields.filter(f => f.required);

      for (let field of requiredFields) {
        const value = this.answers[field.id];

        // Checkbox validation (must be checked)
        if (field.type === 'checkbox') {
          if (value !== true) {
            return false;
          }
          continue;
        }

        // Check if field is filled
        if (!value || value.trim() === '') {
          return false;
        }

        // Email validation
        if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return false;
          }
        }
      }
    }

    return true;
  }

  updateProgress() {
    const progress = ((this.currentStep + 1) / this.visibleSteps.length) * 100;
    gsap.to(this.elements.progressBar, {
      width: `${progress}%`,
      duration: 0.5,
      ease: "power2.out",
    });

    this.elements.currentStepText.textContent = this.currentStep + 1;
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  reset() {
    // Confirm before resetting
    if (!confirm("Möchtest du wirklich alle Eingaben zurücksetzen?")) {
      return;
    }

    // Clear all data
    this.answers = {};
    this.currentStep = 0;
    this.clearLocalStorage();

    // Update visible steps
    this.updateVisibleSteps();

    // Re-render from step 1
    this.renderStep();
    this.updateProgress();
    this.scrollToTop();
  }

  showSummary() {
    // Render summary first
    this.renderSummary();

    // Hide wizard content and navigation
    const timeline = gsap.timeline();

    timeline.to([this.elements.container, this.elements.navigation], {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.elements.container.style.display = 'none';
        this.elements.navigation.style.display = 'none';
      },
    })
    .set(this.elements.summary, { display: 'block' })
    .fromTo(
      this.elements.summary,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.3 }
    );

    this.scrollToTop();
  }

  renderSummary() {
    let html = '<div class="auftrags-finder__summary-list">';

    this.visibleSteps.forEach((step) => {
      const answer = this.answers[step.id];
      if (!answer) return;

      html += `<div class="auftrags-finder__summary-item">`;
      html += `<div class="auftrags-finder__summary-question">${step.title}</div>`;
      html += `<div class="auftrags-finder__summary-answer">`;

      if (step.type === "radio") {
        const option = step.options.find(opt => opt.value === answer);
        if (option) {
          html += `${option.icon ? option.icon + ' ' : ''}${option.label}`;
        }
      } else if (step.type === "checkbox") {
        const selectedOptions = step.options.filter(opt => answer.includes(opt.value));
        html += selectedOptions.map(opt => opt.label).join(', ');
      } else if (step.type === "form") {
        step.fields.forEach((field) => {
          // Skip privacy checkbox in summary
          if (field.type === 'checkbox') return;

          const value = this.answers[field.id];
          if (value) {
            html += `<div><strong>${field.label}:</strong> ${value}</div>`;
          }
        });
      }

      html += `</div></div>`;
    });

    html += '</div>';
    this.elements.summaryContent.innerHTML = html;
  }

  editAnswers() {
    // Hide summary and show wizard content
    const timeline = gsap.timeline();

    timeline.to(this.elements.summary, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.elements.summary.style.display = 'none';
      },
    })
    .set([this.elements.container, this.elements.navigation], { display: 'block' })
    .fromTo(
      [this.elements.container, this.elements.navigation],
      { opacity: 0 },
      { opacity: 1, duration: 0.3 }
    );

    this.scrollToTop();
  }

  async submit() {
    const submitButton = document.querySelector("[data-wizard-submit]");
    submitButton.disabled = true;
    submitButton.textContent = "Wird gesendet...";

    try {
      // Prepare email data
      const emailData = this.prepareEmailData();

      // Encode form data for Netlify
      const formData = new URLSearchParams();
      formData.append('form-name', 'auftrags-finder');

      // Add all data fields
      Object.keys(emailData).forEach(key => {
        formData.append(key, emailData[key]);
      });

      // Send to Netlify Forms
      const response = await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      if (response.ok) {
        this.showSuccess();
      } else {
        throw new Error("Failed to submit");
      }
    } catch (error) {
      console.error("Submission error:", error);
      this.showErrorScreen();
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = "Absenden";
    }
  }

  prepareEmailData() {
    // Prepare beautiful formatted email message
    let emailBody = "NEUE PROJEKT-ANFRAGE\n";
    emailBody += "=".repeat(50) + "\n\n";

    // Contact Information
    emailBody += "KONTAKTDATEN:\n";
    emailBody += "-".repeat(50) + "\n";
    emailBody += `Name: ${this.answers.name || 'Nicht angegeben'}\n`;
    emailBody += `E-Mail: ${this.answers.email || 'Nicht angegeben'}\n`;
    emailBody += `Telefon: ${this.answers.phone || 'Nicht angegeben'}\n`;
    if (this.answers.company) {
      emailBody += `Unternehmen: ${this.answers.company}\n`;
    }
    emailBody += "\n";

    // Project Details
    emailBody += "PROJEKT-DETAILS:\n";
    emailBody += "-".repeat(50) + "\n";

    this.visibleSteps.forEach((step) => {
      const answer = this.answers[step.id];
      if (!answer || step.type === "form") return;

      emailBody += `\n${step.title}\n`;

      if (step.type === "radio") {
        const option = step.options.find(opt => opt.value === answer);
        if (option) {
          emailBody += `→ ${option.label}`;
          if (option.description) {
            emailBody += ` (${option.description})`;
          }
          emailBody += "\n";
        }
      } else if (step.type === "checkbox") {
        const selectedOptions = step.options.filter(opt => answer.includes(opt.value));
        if (selectedOptions.length > 0) {
          selectedOptions.forEach(opt => {
            emailBody += `→ ${opt.label}\n`;
          });
        } else {
          emailBody += "→ Keine Auswahl\n";
        }
      }
    });

    // Project Description
    if (this.answers.message) {
      emailBody += "\nPROJEKT-BESCHREIBUNG:\n";
      emailBody += "-".repeat(50) + "\n";
      emailBody += this.answers.message + "\n";
    }

    emailBody += "\n" + "=".repeat(50) + "\n";
    emailBody += `Eingereicht am: ${new Date().toLocaleString('de-DE')}\n`;

    // Netlify Forms data format
    const data = {
      name: this.answers.name || '',
      email: this.answers.email || '',
      phone: this.answers.phone || '',
      company: this.answers.company || '',
      message: this.answers.message || '',
      'project-details': emailBody
    };

    return data;
  }

  showSuccess() {
    // Clear localStorage after successful submission
    this.clearLocalStorage();

    const timeline = gsap.timeline();

    timeline.to([this.elements.summary, this.elements.progress], {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.elements.summary.style.display = 'none';
        this.elements.progress.style.display = 'none';
      },
    })
    .set(this.elements.success, { display: 'flex' })
    .fromTo(
      this.elements.success,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.5, ease: "back.out(1.7)" }
    );

    this.scrollToTop();
  }

  showErrorScreen() {
    const timeline = gsap.timeline();

    timeline.to([this.elements.summary, this.elements.progress], {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.elements.summary.style.display = 'none';
        this.elements.progress.style.display = 'none';
      },
    })
    .set(this.elements.error, { display: 'flex' })
    .fromTo(
      this.elements.error,
      { opacity: 0 },
      { opacity: 1, duration: 0.3 }
    );

    this.scrollToTop();
  }

  retry() {
    const timeline = gsap.timeline();

    timeline.to(this.elements.error, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.elements.error.style.display = 'none';
      },
    })
    .set([this.elements.summary, this.elements.progress], { display: 'block' })
    .fromTo(
      [this.elements.summary, this.elements.progress],
      { opacity: 0 },
      { opacity: 1, duration: 0.3 }
    );

    this.scrollToTop();
  }
}

// Initialize wizard when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    if (document.querySelector("[data-wizard-container]")) {
      new AuftragsFinderWizard();
    }
  });
} else {
  if (document.querySelector("[data-wizard-container]")) {
    new AuftragsFinderWizard();
  }
}

export default AuftragsFinderWizard;
